/**
 * Open the staff video-call route for a session in a new browser tab.
 *
 * Why a new tab: when the call mounts in the same tab, the practitioner
 * loses their workflow context (session list, drawer, notes-in-progress).
 * Mirrors the Google Meet pattern — call lives in its own tab so the
 * original tab keeps client history visible while the call runs.
 *
 * The `?from=parent` flag lets the call route distinguish "opened from
 * a workflow surface" (close on exit) vs "user navigated directly to
 * the URL" (fall back to in-tab navigation). Combined with `window.opener`
 * being non-null, the route can safely call `window.close()` only when
 * it actually owns a popup tab.
 *
 * Note: we deliberately do NOT pass `noopener` to `window.open`. Both
 * ends are same-origin (our app), so the `noopener` security guidance
 * doesn't apply, and we need `window.opener` so the popup tab can detect
 * its own popup-ness for `window.close()` to be permitted by the browser.
 *
 * Client surface (`/client/sessions/:id/call`) is intentionally NOT
 * routed through this helper — clients keep the existing in-tab UX.
 */
export type StaffCallSurface = 'workspace' | 'clinical';

export const STAFF_CALL_TAB_FLAG = 'parent';

export function openSessionCallTab(sessionId: string, surface: StaffCallSurface): void {
  const base = surface === 'workspace' ? '/admin/workspace' : '/app';
  window.open(
    `${base}/sessions/${encodeURIComponent(sessionId)}/call?from=${STAFF_CALL_TAB_FLAG}`,
    '_blank',
  );
}

/** Read by the staff call routes to decide close-vs-navigate on exit. */
export function isStaffCallPopupTab(): boolean {
  if (typeof window === 'undefined') return false;
  if (!window.opener) return false;
  return new URLSearchParams(window.location.search).get('from') === STAFF_CALL_TAB_FLAG;
}
